import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Briefcase, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/common";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ImageUpload } from "@/components/ImageUpload";

const EMPTY = {
  name: "", industry: "", city: "", service_area: "", description: "",
  logo_url: "", website: "", contact_email: "", contact_phone: "", products: "",
};

export default function MyBusinesses() {
  const [list, setList] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => api.get("/businesses/mine").then(({ data }) => setList(data));

  useEffect(() => {
    load();
    api.get("/meta").then(({ data }) => setIndustries(data.industries)).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b.id);
    setForm({ ...EMPTY, ...b, products: (b.products || []).join(", ") });
    setOpen(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim() || !form.industry || !form.city.trim()) {
      toast.error("Business name, industry and city are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, products: form.products.split(",").map((s) => s.trim()).filter(Boolean) };
      delete payload.id; delete payload.owner_id; delete payload.owner_name;
      delete payload.status; delete payload.is_demo; delete payload.created_at;
      if (editing) {
        await api.put(`/businesses/${editing}`, payload);
        toast.success("Business updated!");
      } else {
        await api.post("/businesses", payload);
        toast.success("Business added!");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/businesses/${deleteId}`);
      toast.success("Business deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleteId(null);
    }
  };

  if (!list) return <div className="container-nx section-nx"><Spinner /></div>;

  return (
    <div className="container-nx section-nx max-w-4xl">
      <Link to="/dashboard" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B192C]">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>

      <div className="flex items-center justify-between">
        <h1 className="heading-2">My businesses</h1>
        <button onClick={openNew} className="btn-gold" data-testid="add-business-btn"><Plus className="h-4 w-4" /> Add business</button>
      </div>

      <div className="mt-8">
        {list.length === 0 ? (
          <EmptyState title="No businesses yet" message="Add your first business listing so the community can discover what you offer." icon={Briefcase}
            action={<button onClick={openNew} className="btn-gold"><Plus className="h-4 w-4" /> Add business</button>} />
        ) : (
          <div className="space-y-4">
            {list.map((b) => (
              <div key={b.id} className="card-nx flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid={`my-business-${b.id}`}>
                <div className="flex items-center gap-4">
                  {b.logo_url ? (
                    <img src={b.logo_url} alt={b.name} className="h-14 w-14 rounded-lg object-cover" />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-[#F5F3EF]"><Briefcase className="h-6 w-6 text-[#B8860B]" /></div>
                  )}
                  <div>
                    <h3 className="font-semibold text-[#0B192C]">{b.name}</h3>
                    <p className="text-xs text-slate-400">{b.industry} · {b.city}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link to={`/businesses/${b.id}`} className="btn-outline-navy px-4 py-2 text-xs">View</Link>
                  <button onClick={() => openEdit(b)} className="btn-outline-navy px-4 py-2 text-xs" data-testid={`edit-business-${b.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</button>
                  <button onClick={() => setDeleteId(b.id)} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid={`delete-business-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit business" : "Add business"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <input value={form.name} onChange={set("name")} placeholder="Business name *" className="input-nx" data-testid="biz-name-input" />
            <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
              <SelectTrigger className="input-nx h-auto" data-testid="biz-industry-select"><SelectValue placeholder="Industry *" /></SelectTrigger>
              <SelectContent>{industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <input value={form.city} onChange={set("city")} placeholder="City *" className="input-nx" data-testid="biz-city-input" />
              <input value={form.service_area} onChange={set("service_area")} placeholder="Service area" className="input-nx" data-testid="biz-service-area-input" />
            </div>
            <textarea value={form.description} onChange={set("description")} placeholder="Description of products / services" className="input-nx min-h-[90px]" data-testid="biz-description-input" />
            <input value={form.products} onChange={set("products")} placeholder="Products / specialities (comma separated)" className="input-nx" data-testid="biz-products-input" />
            <ImageUpload label="Business logo / photo (optional)" value={form.logo_url} onChange={(url) => setForm((f) => ({ ...f, logo_url: url }))} testId="biz-logo" />
            <input value={form.website} onChange={set("website")} placeholder="Website (optional)" className="input-nx" data-testid="biz-website-input" />
            <div className="grid grid-cols-2 gap-3">
              <input value={form.contact_email} onChange={set("contact_email")} placeholder="Contact email" className="input-nx" data-testid="biz-email-input" />
              <input value={form.contact_phone} onChange={set("contact_phone")} placeholder="Contact phone" className="input-nx" data-testid="biz-phone-input" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving} className="btn-gold" data-testid="biz-save-btn">
              {saving ? "Saving..." : editing ? "Save changes" : "Add business"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this business?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-business">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
